/**
 * 랜딩 전환 설정
 *
 * `/` 진입 시 어떤 랜딩(공공데이터 vs AI)을 먼저 보여줄지를
 * 정적 설정파일(public/site-config.json)에서 런타임에 읽어온다.
 * 전환은 이 파일 한 개만 S3에 교체하면 되며, 재빌드가 필요 없다.
 * (전환 스크립트: script/set-landing.js)
 */

export type LandingKey = 'data' | 'ai' | 'main';

export interface SiteConfig {
  defaultLanding: LandingKey;
}

export interface NavLink {
  title: string;
  url: string;
}

/**
 * 설정(landing)에 따른 '홈 관련' 1차 메뉴를 구성한다.
 * - 홈은 항상 루트(/) — 설정된 랜딩이 렌더된다.
 * - 홈이 아닌 랜딩(공공데이터·AI)만 별도 메뉴로 추가한다(= main 은 main 설정에서만 노출).
 *   · main : 홈 · 공공데이터 · AI
 *   · ai   : 홈(AI) · 공공데이터
 *   · data : 홈(공공데이터) · AI
 */
export function homePrimaryNav(landing: LandingKey): NavLink[] {
  const items: NavLink[] = [{ title: '홈', url: '/' }];
  if (landing !== 'data') items.push({ title: '공공데이터', url: '/data' });
  if (landing !== 'ai') items.push({ title: 'AI', url: '/ai' });
  return items;
}

/** 설정파일을 읽지 못하거나 값이 잘못된 경우의 안전한 기본값 */
export const DEFAULT_LANDING: LandingKey = 'data';

// 사이트는 루트(/)에 배포되므로 루트 절대경로로 접근한다.
const CONFIG_PATH = '/site-config.json';

// 메뉴(홈/공공데이터/AI) 구성이 페이지 이동(전체 새로고침)마다 기본값→실제값으로
// 깜빡이며 너비가 출렁이는 것을 막기 위해, 마지막으로 확인된 설정을 캐시한다.
const LANDING_CACHE_KEY = 'pt:defaultLanding';

function isLandingKey(v: unknown): v is LandingKey {
  return v === 'data' || v === 'ai' || v === 'main';
}

/** 캐시된(직전 로드의) 설정값. 첫 렌더를 올바른 메뉴 구성으로 시작하기 위함. */
export function getCachedLanding(): LandingKey {
  try {
    const v = localStorage.getItem(LANDING_CACHE_KEY);
    if (isLandingKey(v)) return v;
  } catch {
    /* localStorage 미사용 환경 무시 */
  }
  return DEFAULT_LANDING;
}

function cacheLanding(key: LandingKey): void {
  try {
    localStorage.setItem(LANDING_CACHE_KEY, key);
  } catch {
    /* 무시 */
  }
}

/**
 * site-config.json 을 캐시 무시하고 읽어 기본 랜딩 키를 반환한다.
 * 네트워크 실패/파싱 실패 시 DEFAULT_LANDING 으로 폴백한다.
 */
export async function fetchDefaultLanding(): Promise<LandingKey> {
  try {
    // CloudFront/브라우저 캐시 우회용 캐시버스팅 쿼리
    const res = await fetch(`${CONFIG_PATH}?ts=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return DEFAULT_LANDING;
    const data = (await res.json()) as Partial<SiteConfig>;
    const key: LandingKey = isLandingKey(data.defaultLanding) ? data.defaultLanding : 'data';
    cacheLanding(key);
    return key;
  } catch {
    return DEFAULT_LANDING;
  }
}
